import { Eye } from "lucide-react";
import type { MouseEvent } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { MentionCandidate } from "../hooks/useMentionCandidates";
import { FileName } from "./FileName";
import { canPreviewMention } from "./files";
import { splitMatch } from "./mention";
import { folderLabel } from "./useThreadWorkspace";

export const MENTION_LIST_ID = "agent-mention-list";
export const mentionOptionId = (index: number) => `${MENTION_LIST_ID}-${index}`;

const keepFocus = (event: MouseEvent) => event.preventDefault(); // the composer keeps the caret

/**
 * The @ list above the composer: this chat's files, then its Files panel's, then the application's
 * documents, the typed letters in bold, each with its type (a Files panel file's folder) and an eye
 * to preview it. Arrow keys and
 * Enter (in the composer) or a tap pick a row.
 */
export function MentionList({
  items,
  query,
  active,
  contextLabel,
  onPick,
  onActive,
  onPreview,
}: {
  items: MentionCandidate[];
  query: string;
  active: number;
  contextLabel: string | null;
  onPick: (candidate: MentionCandidate) => void;
  onActive: (index: number) => void;
  onPreview: (candidate: MentionCandidate) => void;
}) {
  // Each group named by its heading: this chat's files, its Files panel's, the application's documents.
  const groups = [
    { heading: "In this chat", kind: "thread_file" },
    { heading: "From Files", kind: "workspace_file" },
    { heading: contextLabel ? `On ${contextLabel}` : "On this application", kind: "document" },
  ] as const;
  return (
    <div
      id={MENTION_LIST_ID}
      role="listbox"
      aria-label="Files"
      // Phones: full width and flat on the composer's top edge (a sheet would take the keyboard).
      // Desktop: 26rem, or the composer's width when that is narrower.
      className="absolute bottom-full left-0 z-20 mb-2 max-h-72 w-full overflow-y-auto rounded-lg border bg-popover p-1 text-popover-foreground shadow-md max-sm:mb-0 max-sm:rounded-none max-sm:border-x-0 max-sm:shadow-none sm:max-w-[26rem]"
      onMouseDown={keepFocus}
    >
      {groups.map(({ heading, kind }) => {
        const rows = items.flatMap((item, index) => (item.kind === kind ? [{ item, index }] : []));
        if (!rows.length) return null;
        const headingId = `${MENTION_LIST_ID}-${kind}`;
        return (
          <div key={kind} role="group" aria-labelledby={headingId}>
            <div
              id={headingId}
              className="px-2 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
            >
              {heading}
            </div>
            {rows.map(({ item, index }) => {
              const [before, match, after] = splitMatch(item.name, query);
              return (
                <div
                  key={`${item.kind}:${item.id}`}
                  id={mentionOptionId(index)}
                  role="option"
                  aria-selected={index === active}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm",
                    index === active && "bg-accent text-accent-foreground"
                  )}
                  onMouseEnter={() => onActive(index)}
                  onClick={() => onPick(item)}
                >
                  {/* Wrapped, not cut: the name may be all that tells two files apart. */}
                  <span className="min-w-0 flex-1 break-words">
                    <FileName name={before} />
                    <strong className="font-semibold">
                      <FileName name={match} />
                    </strong>
                    <FileName name={after} />
                  </span>
                  {item.kind === "document" && item.type && (
                    <span className="shrink-0 text-xs text-muted-foreground">{item.type}</span>
                  )}
                  {item.kind === "workspace_file" && (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {folderLabel(item.file)}
                    </span>
                  )}
                  {canPreviewMention(item) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      // A pointer shortcut only: the option is the control, and focus stays in
                      // the composer (a chip's own eye previews it once added).
                      tabIndex={-1}
                      aria-hidden
                      className="h-7 w-7 shrink-0 text-muted-foreground max-sm:h-9 max-sm:w-9"
                      onClick={(event) => {
                        event.stopPropagation();
                        onPreview(item);
                      }}
                    >
                      <Eye className="!size-3.5" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
